/**
 * PromptLibraryPanel - the Research prompt library (F-PROMPT).
 *
 * Saved prompts, the user's own and the ones shared with the firm. Picking one
 * fills the composer and does NOT send it: a saved prompt is a starting point
 * the user finishes (a party name, a jurisdiction), the same rule the sample
 * queries follow. What is in the composer can be saved from here, and an org
 * admin can share a prompt with the whole firm.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiMagnifyingGlassLight, PiPencilSimpleLight, PiTrashLight, PiUsersThreeLight,
  PiBookmarkSimpleLight,
} from 'react-icons/pi';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Toggle from '../shared/TailwindComponents/Toggle';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import Badge from '../shared/TailwindComponents/Badge';
import {
  ResearchPrompt, listResearchPrompts, createResearchPrompt, updateResearchPrompt,
  deleteResearchPrompt, recordResearchPromptUse,
} from '../../utils/researchPromptsApi';
import type { ComposerIntent } from './IntentSelector';

export interface PromptLibraryPanelProps {
  /** What is in the composer now - offered for saving. */
  draft?: string;
  intent?: ComposerIntent;
  onInsert: (text: string, intent?: ComposerIntent) => void;
  onClose: () => void;
}

interface FormState {
  uuid?: string;
  title: string;
  body: string;
  isOrgWide: boolean;
}

const PromptLibraryPanel: React.FC<PromptLibraryPanelProps> = ({
  draft = '', intent = null, onInsert, onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector((s: RootState) => !!(s.user as any)?.userInfo?.is_org_admin);

  const [prompts, setPrompts] = useState<ResearchPrompt[] | null>(null);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  const notify = useCallback((modalType: 'success' | 'error', message: string) => {
    dispatch(showFeedbackModal({ showModal: true, modalType, message, duration: 4000 }));
  }, [dispatch]);

  useEffect(() => {
    let alive = true;
    listResearchPrompts()
      .then((rows) => { if (alive) setPrompts(rows); })
      .catch(() => { if (alive) { setPrompts([]); notify('error', t('Could not load your prompts.')); } });
    return () => { alive = false; };
  }, [notify, t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = prompts || [];
    if (!q) return rows;
    return rows.filter((p) => `${p.title} ${p.body}`.toLowerCase().includes(q));
  }, [prompts, query]);
  const mine = filtered.filter((p) => p.is_mine && !p.is_org_wide);
  const firm = filtered.filter((p) => p.is_org_wide);

  const use = (p: ResearchPrompt) => {
    recordResearchPromptUse(p.uuid);
    onInsert(p.body, p.intent ?? undefined);
    onClose();
  };

  const save = async () => {
    if (!form || !form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try {
      const input = { title: form.title.trim(), body: form.body.trim(), is_org_wide: form.isOrgWide };
      if (form.uuid) {
        const updated = await updateResearchPrompt(form.uuid, input);
        setPrompts((prev) => (prev || []).map((p) => (p.uuid === updated.uuid ? updated : p)));
      } else {
        const created = await createResearchPrompt({ ...input, intent: intent ?? null });
        setPrompts((prev) => [created, ...(prev || [])]);
      }
      setForm(null);
      notify('success', t('Prompt saved'));
    } catch (e: any) {
      notify('error', e?.response?.data?.error || t('Could not save the prompt.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (p: ResearchPrompt) => {
    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Delete prompt'),
      message: p.is_org_wide
        ? t('This removes it for everyone in the firm.')
        : t('This cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      onConfirm: async () => {
        try {
          await deleteResearchPrompt(p.uuid);
          setPrompts((prev) => (prev || []).filter((x) => x.uuid !== p.uuid));
        } catch {
          notify('error', t('Could not delete the prompt.'));
        }
      },
    } as any));
  };

  const renderCard = (p: ResearchPrompt) => (
    <div key={p.uuid} className="group rounded-lg border border-hairline-soft bg-surface p-3 shadow-sm" data-testid="prompt-card">
      <button type="button" onClick={() => use(p)} className="block w-full text-start" title={t('Put this prompt in the composer')}>
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-fg-strong truncate">{p.title}</span>
          {p.is_org_wide && <Badge color="gray">{t('Firm')}</Badge>}
        </span>
        <span className="block mt-1 text-xs text-fg-muted line-clamp-3 whitespace-pre-wrap">{p.body}</span>
      </button>
      {p.can_edit && (
        <div className="flex items-center gap-1 mt-2">
          <Button
            variant="neutral"
            size="small"
            icon={<PiPencilSimpleLight size={14} />}
            onClick={() => setForm({ uuid: p.uuid, title: p.title, body: p.body, isOrgWide: p.is_org_wide })}
          >
            {t('Edit')}
          </Button>
          <Button
            variant="neutral"
            size="small"
            icon={<PiTrashLight size={14} />}
            onClick={() => confirmDelete(p)}
          >
            {t('Delete')}
          </Button>
        </div>
      )}
    </div>
  );

  if (form) {
    return (
      <div className="flex flex-col h-full" data-testid="prompt-form">
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <Input
            type="text"
            label={t('Title')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t('e.g. Termination notice check')}
            autoFocus
          />
          <Textarea
            id="prompt-body"
            label={t('Prompt')}
            value={form.body}
            rows={8}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          {isAdmin && (
            <Toggle
              enabled={form.isOrgWide}
              onToggle={(v) => setForm({ ...form, isOrgWide: v })}
              label={t('Share with the firm')}
              description={t('Everyone in the firm can use it. Only admins can change it.')}
            />
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-hairline-soft">
          <Button variant="outline" size="small" onClick={() => setForm(null)}>{t('Cancel')}</Button>
          <Button
            variant="primary"
            size="small"
            onClick={save}
            isLoading={saving}
            disabled={!form.title.trim() || !form.body.trim()}
          >
            {t('Save prompt')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 space-y-2 border-b border-hairline-soft">
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('Search prompts')}
          icon={<PiMagnifyingGlassLight />}
        />
        {draft.trim() && (
          <Button
            variant="outline"
            size="small"
            icon={<PiBookmarkSimpleLight size={14} />}
            onClick={() => setForm({ title: '', body: draft.trim(), isOrgWide: false })}
            className="w-full justify-center"
          >
            {t('Save what’s in the composer')}
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
        {prompts === null ? (
          <Skeleton variant="list" rows={4} />
        ) : (
          <>
            <section className="space-y-2">
              <h3 className="text-[11px] font-medium text-fg-muted">{t('Your prompts')}</h3>
              {mine.length ? mine.map(renderCard) : (
                <p className="text-sm text-fg-muted">
                  {query ? t('No matches.') : t('Write a question in the composer, then save it here to reuse it.')}
                </p>
              )}
            </section>
            {(firm.length > 0 || isAdmin) && (
              <section className="space-y-2">
                <h3 className="text-[11px] font-medium text-fg-muted inline-flex items-center gap-1">
                  <PiUsersThreeLight aria-hidden /> {t('Shared with the firm')}
                </h3>
                {firm.length ? firm.map(renderCard) : (
                  <p className="text-sm text-fg-muted">
                    {query ? t('No matches.') : t('Turn on “Share with the firm” when saving a prompt to add it here.')}
                  </p>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PromptLibraryPanel;
